import React from "react";
import {
  Button,
  Flex,
  Text,
  useColorMode,
  IconButton,
  Tooltip,
  Box,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { TFunction } from "i18next";

// ChapterNavigation component shared between ReadBook and ReadMode
const ChapterNavigation = ({
  currentChapterIndex,
  totalContentItems,
  isFlipping,
  handlePreviousChapter,
  handleNextChapter,
  onOpenTableOfContents,
  t,
  variant = "default",
  nextButtonText,
}: {
  currentChapterIndex: number;
  totalContentItems: number;
  isFlipping: boolean;
  handlePreviousChapter: () => void;
  handleNextChapter: () => void;
  onOpenTableOfContents: () => void;
  t: TFunction;
  variant?: "default" | "fullscreen";
  nextButtonText?: string;
}) => {
  const isFullscreen = variant === "fullscreen";
  const { colorMode } = useColorMode();

  // Get translations directly to avoid potential issues
  const previousText = t("readBook.previousChapter", "Previous");
  const nextText = nextButtonText || t("readBook.nextChapter", "Next");

  return (
    <Box
      width="100%"
      sx={{
        maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
        mx: "auto",
      }}
    >
      <Flex
        direction={{ base: "column", sm: "row" }}
        justify="space-between"
        align="center"
        width="100%"
        gap={{ base: 3, sm: 0 }}
        bg={
          isFullscreen
            ? colorMode === "light"
              ? "rgba(255, 255, 255, 0.85)"
              : "rgba(30, 30, 30, 0.85)"
            : "transparent"
        }
        py={isFullscreen ? 2 : 0}
        px={isFullscreen ? 4 : 0}
        backdropFilter={isFullscreen ? "blur(8px)" : "none"}
        borderRadius={isFullscreen ? "md" : "none"}
        boxShadow={isFullscreen ? "sm" : "none"}
      >
        <Button
          leftIcon={<ChevronLeftIcon boxSize={5} />}
          onClick={handlePreviousChapter}
          isDisabled={currentChapterIndex === 0 || isFlipping}
          variant={isFullscreen ? "ghost" : "outline"}
          size={{ base: "md", sm: "md" }}
          height={{ base: "14", sm: "12" }}
          width={{ base: "full", sm: "auto" }}
          aria-label={previousText}
        >
          {previousText}
        </Button>

        <Text
          fontWeight="medium"
          fontSize={{ base: "lg", sm: "md" }}
          color={colorMode === "light" ? "gray.600" : "gray.300"}
          order={{ base: -1, sm: 0 }}
          mb={{ base: 2, sm: 0 }}
        >
          {currentChapterIndex + 1} / {totalContentItems}
        </Text>

        <Button
          rightIcon={<ChevronRightIcon boxSize={5} />}
          onClick={handleNextChapter}
          isDisabled={
            currentChapterIndex === totalContentItems - 1 || isFlipping
          }
          variant={isFullscreen ? "ghost" : "outline"}
          size={{ base: "md", sm: "md" }}
          height={{ base: "14", sm: "12" }}
          width={{ base: "full", sm: "auto" }}
          aria-label={nextText}
        >
          {nextText}
        </Button>
      </Flex>
    </Box>
  );
};

export default ChapterNavigation;
